import { defineStore } from 'pinia'
import type {
  IReportTemplate,
  TReportTemplateBlock,
  TReportTemplateContentSetup,
} from '@team-milva/lab-models/resources'
import type { IImageBase } from '@team-milva/lab-models/image'
import {
  collection,
  getDocs,
  onSnapshot,
  query,
  where,
  writeBatch,
} from 'firebase/firestore'
import { db } from '@/firebase/init'
import { managementApi } from '@/globals/typescript/utils/api/managementClient'
import { useAccountStore } from '@/stores/accountStore'
import { ReportTemplate } from '@/globals/typescript/models/ReportTemplate'

export { ReportTemplate }

type CreateReportTemplateParams = {
  title: string
  displayTitle?: string
  type: 'lab' | 'customer'
  contentSetup?: TReportTemplateContentSetup
}

type UpdateReportTemplateParams = {
  title?: Record<string, string>
  displayTitle?: Record<string, string> | null
  type?: 'lab' | 'customer'
  contentSetup?: TReportTemplateContentSetup
  blocks?: TReportTemplateBlock[]
  previewVariables?: Record<string, string>
  partnerLogo?: IImageBase | null
}

export const useReportTemplateStore = defineStore('reportTemplate', {
  state: () => ({
    allReportTemplates: [] as ReportTemplate[],
    unsubscribeAllReportTemplates: null as (() => void) | null,
  }),

  getters: {
    allReportTemplatesLoaded(): boolean {
      return !!this.unsubscribeAllReportTemplates
    },
    hasReportTemplates(): boolean {
      return this.allReportTemplates.length > 0
    },
  },

  actions: {
    getReportTemplates(): Promise<ReportTemplate[]> {
      if (this.allReportTemplatesLoaded) {
        return Promise.resolve(this.allReportTemplates)
      }

      return new Promise((resolve) => {
        let resolved = false

        const accountStore = useAccountStore()

        const q = query(
          collection(db, 'Apps', '-lab-management-', 'ReportTemplates'),
          where('labAccountID', '==', accountStore.labAccount?.id)
        )

        const unsubscribe = onSnapshot(q, (querySnapshot) => {
          this.allReportTemplates = querySnapshot.docs
            .map((doc) => new ReportTemplate(doc.data() as IReportTemplate))
            .sort((a, b) => a.timestamps.createdAt - b.timestamps.createdAt)

          this.unsubscribeAllReportTemplates = unsubscribe

          if (!resolved) {
            resolved = true
            resolve(this.allReportTemplates)
          }
        })
      })
    },

    async createReportTemplate(
      params: CreateReportTemplateParams
    ): Promise<void> {
      const accountStore = useAccountStore()
      const labAccountID = accountStore.labAccount?.id ?? ''

      const result = await managementApi(
        'post',
        'lab-accounts/{labAccountID}/report-templates',
        {
          path: { labAccountID },
          body: {
            title: params.title,
            displayTitle: params.displayTitle,
            type: params.type,
            contentSetup: params.contentSetup,
          },
        }
      )
      if (!result.success) throw new Error(result.code)
    },

    async updateReportTemplate(
      reportTemplateID: string,
      params: UpdateReportTemplateParams
    ): Promise<void> {
      const accountStore = useAccountStore()
      const labAccountID = accountStore.labAccount?.id ?? ''

      const response = await managementApi(
        'patch',
        'lab-accounts/{labAccountID}/report-templates/{reportTemplateID}',
        {
          path: { labAccountID, reportTemplateID },
          body: params,
        }
      )
      if (!response.success) {
        throw new Error(response.message ?? 'Update report template failed')
      }
    },

    // DEV: remove before launch
    async deleteAllReportTemplates(): Promise<void> {
      const accountStore = useAccountStore()
      const labAccountID = accountStore.labAccount?.id ?? ''

      const snap = await getDocs(
        query(
          collection(db, 'Apps', '-lab-management-', 'ReportTemplates'),
          where('labAccountID', '==', labAccountID)
        )
      )

      const batch = writeBatch(db)
      snap.docs.forEach((x) => batch.delete(x.ref))
      await batch.commit()
    },

    resetStore(): void {
      this.allReportTemplates = []
      if (this.unsubscribeAllReportTemplates) {
        this.unsubscribeAllReportTemplates()
        this.unsubscribeAllReportTemplates = null
      }
    },
  },
})

